import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { router } from "@inertiajs/react"
import { IconArchive, IconRestore } from "@tabler/icons-react"
import { useState } from "react"
import { type BoardFieldDefinition, FIELD_TYPE_LABELS, type FieldType, choiceType } from "../types"

interface FieldsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  boardKey: string
  fields: BoardFieldDefinition[]
}

const reload = { preserveScroll: true, only: ["fields"] }

export function FieldsDialog({ open, onOpenChange, boardKey, fields }: FieldsDialogProps) {
  const [name, setName] = useState("")
  const [fieldType, setFieldType] = useState<FieldType>("text")
  const [choices, setChoices] = useState("")

  const active = fields.filter((field) => !field.archived)
  const retired = fields.filter((field) => field.archived)

  const addField = () => {
    if (!name.trim()) return

    router.post(
      `/boards/${boardKey}/fields`,
      {
        board_field: {
          name,
          field_type: fieldType,
          options: choiceType(fieldType)
            ? choices
                .split(",")
                .map((choice) => choice.trim())
                .filter(Boolean)
            : [],
        },
      },
      {
        ...reload,
        onSuccess: () => {
          setName("")
          setChoices("")
          setFieldType("text")
        },
      },
    )
  }

  const rename = (field: BoardFieldDefinition, value: string) =>
    router.patch(`/boards/${boardKey}/fields/${field.id}`, { board_field: { name: value } }, reload)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Fields on this board</DialogTitle>
          <DialogDescription>
            Every ticket here gets these, on top of assignee, priority, due date and estimate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {active.length === 0 && <p className="text-muted-foreground text-sm">No fields yet.</p>}

          {active.map((field) => (
            <div key={field.id} className="flex items-center gap-2">
              <Input
                defaultValue={field.name}
                aria-label={`Name of ${field.name}`}
                onBlur={(event) => event.target.value !== field.name && rename(field, event.target.value)}
              />

              <span className="text-muted-foreground w-28 shrink-0 text-xs">{FIELD_TYPE_LABELS[field.field_type]}</span>

              <Button
                variant="ghost"
                size="icon"
                aria-label={`Retire ${field.name}`}
                onClick={() => router.patch(`/boards/${boardKey}/fields/${field.id}/archive`, {}, reload)}
              >
                <IconArchive className="size-4" />
              </Button>
            </div>
          ))}
        </div>

        {retired.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-medium uppercase">Retired</p>
              {retired.map((field) => (
                <div key={field.id} className="flex items-center gap-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">{field.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Restore ${field.name}`}
                    onClick={() => router.patch(`/boards/${boardKey}/fields/${field.id}/restore`, {}, reload)}
                  >
                    <IconRestore className="size-4" />
                    Restore
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}

        <Separator />

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="field_name">Name</Label>
              <Input id="field_name" value={name} onChange={(event) => setName(event.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="field_type">Type</Label>
              <Select value={fieldType} onValueChange={(value) => setFieldType(value as FieldType)}>
                <SelectTrigger id="field_type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {choiceType(fieldType) && (
            <div className="space-y-2">
              <Label htmlFor="field_choices">Choices</Label>
              <Input
                id="field_choices"
                value={choices}
                placeholder="Low, Medium, High"
                onChange={(event) => setChoices(event.target.value)}
              />
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={addField} disabled={!name.trim()}>
              Add field
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
